import type { FunctionComponent, PropsWithChildren } from 'react';
import { useMemo } from 'react';
import {
    SidebarLayout as Layout,
    isOfTypeNavEntryGroup,
    type SidebarEntry,
} from '@starjack/layout';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useSlice } from '@/hooks/useSlice';
import { pageDataSlice } from '@starjack/features/pageData';
import { useIsAuthorized } from '@/hooks/useIsAuthorized';
import { Permission, UserType } from '@/models/user';
import {
    items,
    type BackofficeNavEntryGroup,
    type BackofficeNavEntryItem,
} from '@/layouts/sidebar/config';
import { AccountPopover } from '@/components/AccountPopover/AccountPopover';
import StarjackLogo from '@/assets/logo/starjack-head-neg.png';

export { SIDE_NAV_WIDTH } from '@starjack/layout';

export function useResolvedSidebarEntries(): SidebarEntry[] {
    const { userType, permissions } = useAuthentication();
    const [, { checkPermissions }] = useIsAuthorized(
        permissions as unknown as Permission[]
    );
    return useMemo(() => {
        if ((userType as unknown as UserType) === UserType.LiftOperator) {
            return toPackageEntries(items);
        }
        const authorized = items.reduce<
            (BackofficeNavEntryGroup | BackofficeNavEntryItem)[]
        >((acc, current) => {
            if (isOfTypeNavEntryGroup(current as SidebarEntry)) {
                const group = current as BackofficeNavEntryGroup;
                const filtered = group.entries.filter((item) =>
                    checkPermissions(item.permissionsRequired)
                );
                if (filtered.length) acc.push({ ...group, entries: filtered });
            } else {
                const item = current as BackofficeNavEntryItem;
                if (checkPermissions(item.permissionsRequired)) acc.push(item);
            }
            return acc;
        }, []);
        return toPackageEntries(authorized);
    }, [userType, checkPermissions]);
}

function toPackageEntries(
    entries: (BackofficeNavEntryGroup | BackofficeNavEntryItem)[]
): SidebarEntry[] {
    return entries.map((e) => {
        if (isOfTypeNavEntryGroup(e as SidebarEntry)) {
            const group = e as BackofficeNavEntryGroup;
            return {
                ...group,
                entries: group.entries.map((entry) => ({
                    ...entry,
                    permissionsRequired: entry.permissionsRequired.map(String),
                })),
            };
        }
        const item = e as BackofficeNavEntryItem;
        return {
            ...item,
            permissionsRequired: item.permissionsRequired.map(String),
        };
    });
}

export const SidebarLayout: FunctionComponent<PropsWithChildren> = ({
    children,
}) => {
    const { liftOperatorData } = useAuthentication();
    const {
        actions: { resetPageData },
        state: { pageDescription, explanationVideo },
    } = useSlice(pageDataSlice, 'requestSlice');

    const entries = useResolvedSidebarEntries();

    return (
        <Layout
            entries={entries}
            logo={{ src: StarjackLogo, alt: 'Starjack Logo' }}
            appName='starjack backoffice'
            pageDescription={pageDescription}
            explanationVideo={explanationVideo}
            resetPageData={resetPageData}
            userInitial={liftOperatorData?.skiOperator?.[0]}
            renderAccountPopover={({ anchorEl, open, onClose }) => (
                <AccountPopover
                    anchorEl={anchorEl}
                    open={open}
                    onClose={onClose}
                />
            )}
        >
            {children}
        </Layout>
    );
};
